import { useEffect, useRef, useState, useCallback } from "react";
import { WS_URL } from "./constants";
import { getUserKey } from "./storage";

export interface LogStreamMessage {
  type: "log" | "status" | "connected" | "pong";
  testRunId: number;
  timestamp?: string;
  logType?: string;
  message?: string;
  ecsTaskArn?: string;
  testRunTestIds?: number[];
  metadata?: Record<string, unknown>;
}

export interface UseLogStreamOptions {
  testRunId: number;
  enabled?: boolean;
  onMessage?: (message: LogStreamMessage) => void;
  onError?: (error: Event) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export interface UseLogStreamResult {
  isConnected: boolean;
  isConnecting: boolean;
  messages: LogStreamMessage[];
  error: string | null;
  connect: () => void;
  disconnect: () => void;
}

/**
 * Hook for streaming test run logs via WebSocket.
 * Automatically handles connection, authentication, reconnection, and message buffering.
 */
export function useLogStream({
  testRunId,
  enabled = true,
  onMessage,
  onError,
  onConnected,
  onDisconnected,
  reconnectInterval = 3000,
  maxReconnectAttempts = 5,
}: UseLogStreamOptions): UseLogStreamResult {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<LogStreamMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close(1000, "Client disconnect");
      wsRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
  }, [clearTimers]);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    const token = getUserKey();
    if (!token) {
      setError("Not authenticated");
      return;
    }

    setIsConnecting(true);
    setError(null);

    const wsUrl = `${WS_URL}/ws/test-runs/${testRunId}/logs?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      setIsConnected(true);
      setIsConnecting(false);
      setError(null);
      reconnectAttemptsRef.current = 0;

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send("ping");
        }
      }, 30000);

      onConnected?.();
    };

    ws.onmessage = (event) => {
      try {
        const message: LogStreamMessage = JSON.parse(event.data);

        if (message.type === "pong") {
          return; // Ignore pong responses
        }

        setMessages((prev) => [...prev, message]);
        onMessage?.(message);
      } catch (e) {
        console.error("Failed to parse WebSocket message:", e);
      }
    };

    ws.onerror = (event) => {
      setError("WebSocket error");
      onError?.(event);
    };

    ws.onclose = (event) => {
      clearTimers();
      setIsConnected(false);
      setIsConnecting(false);
      wsRef.current = null;

      onDisconnected?.();

      // Attempt reconnection if not a clean close
      if (event.code !== 1000 && enabled && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 1);
        console.log(`WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      } else if (event.code !== 1000) {
        setError(`Connection closed: ${event.reason || "Unknown reason"}`);
      }
    };

    wsRef.current = ws;
  }, [testRunId, enabled, onMessage, onError, onConnected, onDisconnected, reconnectInterval, maxReconnectAttempts, clearTimers]);

  // Connect when enabled and testRunId changes
  useEffect(() => {
    if (enabled && testRunId) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [testRunId, enabled, connect, disconnect]);

  // Clear messages when testRunId changes
  useEffect(() => {
    setMessages([]);
  }, [testRunId]);

  return {
    isConnected,
    isConnecting,
    messages,
    error,
    connect,
    disconnect,
  };
}

/**
 * Format a log stream message for display in the terminal.
 * Returns the message with timestamp prefix.
 */
export function formatLogMessage(message: LogStreamMessage): string {
  const timestamp = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      } as Intl.DateTimeFormatOptions)
    : "";

  const prefix = timestamp ? `${timestamp} - ` : "";

  switch (message.type) {
    case "log":
      return `${prefix}${message.message || ""}`;
    case "status":
      return `${prefix}[STATUS] ${message.message || ""}`;
    case "connected":
      return `${prefix}[CONNECTED] ${message.message || "Connected to log stream"}`;
    default:
      return `${prefix}${message.message || ""}`;
  }
}
